import { Autocomplete, Checkbox, Stack } from '@mui/material';
import { Show, TextField } from '@side/pantry';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Spinner } from '@/components/Spinner';
import { useCore, useIdentity } from '$api-clients/hooks';
import { AgentFilter } from '../components/AgentFilter';
import { CloseDateFilter } from '../components/CloseDateFilter';
import { CustomDateRangePickers } from '../components/CustomDateRangePickers';
import { HideTerminatedAgentsFilter } from '../components/HideTerminatedAgentsFilter';
import { TeamFilter } from '../components/TeamFilter';
import { ReportFiltersDrawer } from '../ReportFiltersDrawer/ReportFiltersDrawer';
import { useReportsConfiguration } from '../ReportsConfiguration/useReportsConfiguration';
import {
  useReportsRole,
  useRoleCanSee,
} from '../ReportsConfiguration/useReportsRole';
import { REPORTING_PERIOD_CUSTOM } from '../types';
import {
  type CompensationReportFiltersFormValues,
  compensationReportFiltersDefaultValues,
  type TransactionTypeOption,
  useCompensationReportFilters,
} from './CompensationReportFilters.helpers';

interface Props {
  open: boolean;
  onClose: () => void;
  viewReportAs: string;
}

const transactionTypeOptions: TransactionTypeOption[] = [
  { label: 'Listing', value: 'LISTING' },
  { label: 'Offer', value: 'OFFER' },
  { label: 'Referral', value: 'REFERRAL' },
  { label: 'Lease (Landlord)', value: 'LEASE_LANDLORD' },
  { label: 'Lease (Tenant)', value: 'LEASE_TENANT' },
];

export function CompensationReportFilters({
  open,
  onClose,
  viewReportAs,
}: Props) {
  const { data: userinfo, isLoading: isLoadingUser } = useIdentity(
    '/users/{userId}',
    { params: { path: { userId: viewReportAs } } },
  );
  const { data: config, isLoading: isLoadingConfig } =
    useReportsConfiguration();
  const { isAgent } = useReportsRole();
  const { roleCanSee } = useRoleCanSee();
  const { filters, setFilters } = useCompensationReportFilters();
  const {
    control,
    setValue,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = useForm<CompensationReportFiltersFormValues>({
    defaultValues: filters,
  });
  const { data: leadSourcesData = [], isLoading: isLoadingLeadSources } =
    useCore('/lead-sources');

  const leadSourceOptions = useMemo(
    () =>
      leadSourcesData.flatMap((source) =>
        source !== null ? { label: source.name, value: source.id } : [],
      ),
    [leadSourcesData],
  );
  const [
    reportingPeriod,
    agents,
    teams,
    transactionType,
    hideTerminatedAgents,
    leadSources,
  ] = useWatch({
    control,
    name: [
      'reportingPeriod',
      'agents',
      'teams',
      'transactionType',
      'hideTerminatedAgents',
      'leadSources',
    ],
  });
  const isCustomRange = reportingPeriod === REPORTING_PERIOD_CUSTOM;
  // We only want to disable the clear button if the values are the same as the default values
  // isDirty can not be used because it is set to true when the form is cleared.
  const isClearDisabled = useMemo(
    () =>
      isEqual(
        {
          reportingPeriod,
          agents,
          teams,
          transactionType,
          hideTerminatedAgents,
          leadSources,
        },
        compensationReportFiltersDefaultValues,
      ),
    [
      reportingPeriod,
      agents,
      teams,
      transactionType,
      hideTerminatedAgents,
      leadSources,
    ],
  );
  const isLoading = isLoadingUser || isLoadingConfig || isLoadingLeadSources;

  useEffect(() => {
    if (open) {
      reset(filters);
    }
  }, [open]);

  const handleFilterToOnlyMe = () => {
    if (!userinfo) return;

    setValue('agents', [{ label: userinfo.displayName, value: userinfo.id }], {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (!open) {
      reset(filters);
    }
  }, [open, filters]);

  const onSubmitCallback = (data: CompensationReportFiltersFormValues) => {
    setFilters(data);
    onClose();
  };
  const handleClear = () => {
    // we need to use setValue vs reset to ensure the form gets marked as dirty
    setValue(
      'reportingPeriod',
      compensationReportFiltersDefaultValues.reportingPeriod,
      {
        shouldDirty: true,
      },
    );
    // `undefined` means "omit / keep default" in RHF, so the field would not clear.
    // `null` is an explicit empty value (matches CustomDateRangePickers after clear).
    // Cast: form types use `string | undefined`; `null` is not in the interface but is valid at runtime.
    setValue('customDateStart', null as never, { shouldDirty: true });
    setValue('customDateEnd', null as never, { shouldDirty: true });
    setValue('agents', compensationReportFiltersDefaultValues.agents, {
      shouldDirty: true,
    });
    setValue('teams', compensationReportFiltersDefaultValues.teams, {
      shouldDirty: true,
    });
    setValue(
      'transactionType',
      compensationReportFiltersDefaultValues.transactionType,
      {
        shouldDirty: true,
      },
    );
    setValue(
      'hideTerminatedAgents',
      compensationReportFiltersDefaultValues.hideTerminatedAgents,
      {
        shouldDirty: true,
      },
    );
    setValue(
      'leadSources',
      compensationReportFiltersDefaultValues.leadSources,
      {
        shouldDirty: true,
      },
    );
  };

  return (
    <ReportFiltersDrawer
      open={open}
      onClose={onClose}
      title="Compensation Report"
      isSubmitDisabled={!isDirty}
      onClear={handleClear}
      onSubmit={handleSubmit(onSubmitCallback)}
      isClearDisabled={isClearDisabled}
    >
      {isLoading ? (
        <Spinner centered />
      ) : (
        <Stack direction="column" sx={{ gap: 3 }}>
          <CloseDateFilter
            name="reportingPeriod"
            control={control}
            startDate={config?.closingDateStart}
          />
          <Show when={isCustomRange}>
            <CustomDateRangePickers
              control={control}
              setValue={setValue}
              eventContainerPrefix="compensation-report"
              required={isCustomRange}
              minDate={config?.closingDateStart}
            />
          </Show>
          <Show when={!isAgent}>
            <AgentFilter
              name="agents"
              control={control}
              agentIdentityIds={config?.agentIdentityIds}
              onFilterToOnlyMe={handleFilterToOnlyMe}
            />
          </Show>
          <Show when={roleCanSee('team')}>
            <TeamFilter
              name="teams"
              control={control}
              teamIdentityIds={config?.teamIdentityIds}
            />
          </Show>
          <Controller
            name="leadSources"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={leadSourceOptions}
                value={field.value}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_event, newValue) => field.onChange(newValue)}
                renderOption={(props, option, { selected }) => (
                  <Stack direction="row" component="li" {...props}>
                    <Checkbox
                      disableRipple
                      checked={selected}
                      sx={{ mr: 2, p: 0 }}
                    />
                    {option.label}
                  </Stack>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lead Source"
                    placeholder="Select to filter"
                    fullWidth
                  />
                )}
              />
            )}
          />
          <Controller
            name="transactionType"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={transactionTypeOptions}
                value={field.value.map((v) =>
                  transactionTypeOptions.find((t) => t.value === v),
                )}
                onChange={(_event, newValue) =>
                  field.onChange(
                    newValue.map((v) => (typeof v === 'string' ? v : v?.value)),
                  )
                }
                isOptionEqualToValue={(option, val) =>
                  option?.value === val?.value
                }
                renderOption={(props, option) => (
                  <Stack direction="row" component="li" {...props}>
                    <Checkbox
                      disableRipple
                      checked={
                        option?.value && field.value.includes(option.value)
                      }
                      sx={{ mr: 2, p: 0 }}
                    />
                    {option?.label}
                  </Stack>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Transaction Type"
                    placeholder="Select to filter"
                    fullWidth
                  />
                )}
              />
            )}
          />
          <HideTerminatedAgentsFilter
            name="hideTerminatedAgents"
            control={control}
          />
        </Stack>
      )}
    </ReportFiltersDrawer>
  );
}
